import { pageWrapper } from '@/Styles';
import { PageHeader } from '@/components/PageHeader';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import {
  IconLayout,
  IconBuildingFactory2,
  IconListDetails,
  IconPhoto,
  IconStack2,
} from '@tabler/icons-react';
import {
  Avatar,
  Box,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { cdnCompanyLogos, cdnLayoutImages, layoutPath, userPath, websiteDomain } from '../ApiPaths';
import { IProduct } from '@/types/Product.model';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { getJson } from '@/services/httpClient';
import { UserProfile } from '@/store/Store';
import { useTranslation } from 'react-i18next';
import classes from './LayoutViewer.module.css';

type StructureRow = {
  objID: string;
  name: string;
  count: number;
};

export function LayoutViewer() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [productOwner, setProductOwner] = useState<Partial<UserProfile>>();
  const [layout, setLayout] = useState<Partial<IProduct>>({
    productName: '',
    productPage: '',
    owningUser_id: '',
    productImage: '',
    availableFormats: [],
    layout: '',
    views: 0,
  });
  const [structureRows, setStructureRows] = useState<StructureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setPreviewFailed(false);

    getJson<Partial<IProduct>>(layoutPath + id)
      .then((layoutData) => {
        if (cancelled) return;
        setLayout(layoutData);

        try {
          const sceneDescription: SceneModel[] = JSON.parse(layoutData.layout as string);
          const grouped = sceneDescription.reduce<Record<string, StructureRow>>((acc, item) => {
            const { objID, filename } = item;
            if (!acc[objID]) {
              acc[objID] = {
                objID,
                name: cleanFilename(filename),
                count: 0,
              };
            }
            acc[objID].count += 1;
            return acc;
          }, {});

          setStructureRows(
            Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
          );
        } catch {
          setStructureRows([]);
        }

        if (!layoutData.owningUser_id) return;
        return getJson<Partial<UserProfile>>(userPath + layoutData.owningUser_id)
          .then((owner) => {
            if (!cancelled) setProductOwner(owner);
          })
          .catch((error) => console.error(error));
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const accentColor = productOwner?.qrCodeColor || DEFAULT_QR_CODE_COLOR;
  const accentStyle = { '--layout-accent': accentColor } as CSSProperties;
  const totalPlacements = useMemo(
    () => structureRows.reduce((sum, row) => sum + row.count, 0),
    [structureRows]
  );
  const ownerInitials = (productOwner?.username ?? productOwner?.companyName ?? '?')
    .slice(0, 2)
    .toUpperCase();

  const openOwnerProfile = () => {
    if (!productOwner?.username) return;
    window.location.href = `${websiteDomain}/user/${productOwner.username}`;
  };

  return (
    <Center style={pageWrapper}>
      <Box className={classes.page}>
        <Paper
          shadow="xs"
          radius="lg"
          p="lg"
          withBorder
          mb="xl"
          className={classes.hero}
          style={accentStyle}
        >
          <div className={classes.heroRow}>
            <PageHeader
              icon={<IconLayout size={22} stroke={1.75} />}
              title={layout.productName || t('marketplace.entities.layouts')}
              subtitle={t('marketplace.entities.layouts')}
              accentColor={accentColor}
              mb={0}
            />

            {!loading ? (
              <div className={classes.stats}>
                <div className={classes.statCard}>
                  <span className={classes.statIcon} aria-hidden>
                    <IconStack2 size={18} stroke={1.75} />
                  </span>
                  <span className={classes.statMeta}>
                    <span className={classes.statValue}>{structureRows.length}</span>
                    <span className={classes.statLabel}>{t('layoutViewer.structure')}</span>
                  </span>
                </div>
                <div className={classes.statCard}>
                  <span className={classes.statIcon} aria-hidden>
                    <IconListDetails size={18} stroke={1.75} />
                  </span>
                  <span className={classes.statMeta}>
                    <span className={classes.statValue}>{totalPlacements}</span>
                    <span className={classes.statLabel}>{t('layoutViewer.placements')}</span>
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </Paper>

        <Paper shadow="xs" radius="lg" withBorder className={classes.shell} style={accentStyle}>
          {loading ? (
            <div className={classes.loadingShell}>
              <Loader color={accentColor} />
            </div>
          ) : (
            <div className={classes.grid}>
              <section className={classes.column}>
                <div className={classes.columnHeader}>
                  <Group gap="xs">
                    <span className={classes.statIcon} aria-hidden>
                      <IconBuildingFactory2 size={16} stroke={1.75} />
                    </span>
                    <span className={classes.columnTitle}>{t('layoutViewer.owner')}</span>
                  </Group>
                </div>
                <div className={classes.ownerBody}>
                  {productOwner?.companyLogo ? (
                    <img
                      src={`${cdnCompanyLogos}${productOwner.companyLogo}`}
                      alt=""
                      className={classes.ownerLogo}
                    />
                  ) : (
                    <Avatar
                      radius="xl"
                      size={72}
                      color={accentColor}
                      style={{ cursor: productOwner?.username ? 'pointer' : 'default' }}
                      onClick={productOwner?.username ? openOwnerProfile : undefined}
                    >
                      {ownerInitials}
                    </Avatar>
                  )}

                  <Stack gap={4} align="center">
                    {productOwner?.companyName ? (
                      <Text className={classes.ownerName}>{productOwner.companyName}</Text>
                    ) : null}
                    <Text
                      className={`${classes.ownerName} ${productOwner?.username ? classes.ownerUsername : ''}`}
                      onClick={productOwner?.username ? openOwnerProfile : undefined}
                    >
                      {productOwner?.username
                        ? `@${productOwner.username}`
                        : t('layoutViewer.noUsername')}
                    </Text>
                    {!productOwner?.companyName ? (
                      <Text className={classes.ownerCompany}>{t('layoutViewer.noCompanyName')}</Text>
                    ) : null}
                  </Stack>
                </div>
              </section>

              <section className={classes.column}>
                <div className={classes.columnHeader}>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs">
                      <span className={classes.statIcon} aria-hidden>
                        <IconListDetails size={16} stroke={1.75} />
                      </span>
                      <span className={classes.columnTitle}>{t('layoutViewer.structure')}</span>
                    </Group>
                    {structureRows.length > 0 ? (
                      <span className={classes.countBadge}>{structureRows.length}</span>
                    ) : null}
                  </Group>
                </div>

                <div className={classes.structureBody}>
                  {structureRows.length === 0 ? (
                    <div className={classes.emptyState}>{t('layoutViewer.emptyStructure')}</div>
                  ) : (
                    <div className={classes.structureList}>
                      {structureRows.map((row) => (
                        <div key={row.objID} className={classes.structureRow}>
                          <span className={classes.structureName}>{row.name}</span>
                          <span className={classes.countBadge}>{row.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section className={classes.column}>
                <div className={classes.columnHeader}>
                  <Group gap="xs">
                    <span className={classes.statIcon} aria-hidden>
                      <IconPhoto size={16} stroke={1.75} />
                    </span>
                    <span className={classes.columnTitle}>{t('layoutViewer.preview')}</span>
                  </Group>
                </div>
                <div className={classes.previewBody}>
                  {layout._id && !previewFailed ? (
                    <div className={classes.previewFrame}>
                      <img
                        src={`${cdnLayoutImages}${layout._id}.png`}
                        alt={t('layoutViewer.previewAlt')}
                        className={classes.previewImage}
                        onError={() => setPreviewFailed(true)}
                      />
                    </div>
                  ) : (
                    <Stack align="center" gap="xs" py="lg">
                      <span className={classes.statIcon} aria-hidden>
                        <IconLayout size={20} stroke={1.75} />
                      </span>
                      <Text size="sm" c="dimmed" ta="center">
                        {t('layoutViewer.previewUnavailable')}
                      </Text>
                    </Stack>
                  )}
                </div>
              </section>
            </div>
          )}
        </Paper>
      </Box>
    </Center>
  );
}

/**
 * Removes the underscore followed by a UUID from the filename.
 */
function cleanFilename(filename: string): string {
  return filename.replace(/_[0-9a-fA-F-]{36}$/, '');
}
